/**
 * Account Status Badge Components
 *
 * Visual status indicators for account and billing states.
 * Uses a refined, utilitarian aesthetic appropriate for admin tooling.
 */
import { cn } from '@respectly/ui';
import type { TenantStatus, BillingStatus } from '@respectly/shared-types';

// ============================================
// Status Configurations
// ============================================

const statusConfig: Record<
  TenantStatus,
  { label: string; dotColor: string; badgeClass: string }
> = {
  active: {
    label: 'Active',
    dotColor: 'bg-emerald-500',
    badgeClass: 'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 border-emerald-500/20',
  },
  trial: {
    label: 'Trial',
    dotColor: 'bg-blue-500',
    badgeClass: 'bg-blue-500/10 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400 border-blue-500/20',
  },
  suspended: {
    label: 'Suspended',
    dotColor: 'bg-amber-500',
    badgeClass: 'bg-amber-500/10 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 border-amber-500/20',
  },
  cancelled: {
    label: 'Cancelled',
    dotColor: 'bg-red-500',
    badgeClass: 'bg-red-500/10 text-red-700 dark:bg-red-500/20 dark:text-red-400 border-red-500/20',
  },
};

const billingStatusConfig: Record<
  BillingStatus,
  { label: string; dotColor: string; badgeClass: string }
> = {
  current: {
    label: 'Current',
    dotColor: 'bg-emerald-500',
    badgeClass: 'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 border-emerald-500/20',
  },
  past_due: {
    label: 'Past Due',
    dotColor: 'bg-amber-500',
    badgeClass: 'bg-amber-500/10 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 border-amber-500/20',
  },
  grace_period: {
    label: 'Grace Period',
    dotColor: 'bg-amber-500',
    badgeClass: 'bg-amber-500/10 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 border-amber-500/20',
  },
  suspended: {
    label: 'Suspended',
    dotColor: 'bg-red-500',
    badgeClass: 'bg-red-500/10 text-red-700 dark:bg-red-500/20 dark:text-red-400 border-red-500/20',
  },
  pending_card: {
    label: 'Pending Card',
    dotColor: 'bg-slate-400',
    badgeClass: 'bg-slate-500/10 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300 border-slate-500/20',
  },
};

const unknownStatusConfig = {
  label: 'Unknown',
  dotColor: 'bg-slate-400',
  badgeClass: 'bg-slate-500/10 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300 border-slate-500/20',
};

function formatUnknownLabel(value: string): string {
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ') || 'Unknown';
}

// ============================================
// Account Status Badge
// ============================================

interface AccountStatusBadgeProps {
  status: TenantStatus;
  className?: string;
  showDot?: boolean;
  size?: 'sm' | 'md';
}

export function AccountStatusBadge({
  status,
  className,
  showDot = true,
  size = 'md',
}: AccountStatusBadgeProps) {
  const config = statusConfig[status] ?? {
    ...unknownStatusConfig,
    label: status ? formatUnknownLabel(status) : unknownStatusConfig.label,
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs',
        config.badgeClass,
        className
      )}
    >
      {showDot && (
        <span
          className={cn(
            'rounded-full',
            size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2',
            config.dotColor
          )}
        />
      )}
      {config.label}
    </span>
  );
}

// ============================================
// Billing Status Badge
// ============================================

interface BillingStatusBadgeProps {
  status: BillingStatus;
  className?: string;
  showDot?: boolean;
  size?: 'sm' | 'md';
}

export function BillingStatusBadge({
  status,
  className,
  showDot = true,
  size = 'md',
}: BillingStatusBadgeProps) {
  const config = billingStatusConfig[status] ?? {
    ...unknownStatusConfig,
    label: status ? formatUnknownLabel(status) : unknownStatusConfig.label,
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs',
        config.badgeClass,
        className
      )}
    >
      {showDot && (
        <span
          className={cn(
            'rounded-full',
            size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2',
            config.dotColor
          )}
        />
      )}
      {config.label}
    </span>
  );
}

// ============================================
// Utility: Get status color for charts/icons
// ============================================

export function getStatusColor(status: TenantStatus): string {
  const colors: Record<TenantStatus, string> = {
    active: '#10b981', // emerald-500
    trial: '#3b82f6', // blue-500
    suspended: '#f59e0b', // amber-500
    cancelled: '#ef4444', // red-500
  };
  return colors[status];
}

export function getBillingStatusColor(status: BillingStatus): string {
  const colors: Record<BillingStatus, string> = {
    current: '#10b981', // emerald-500
    past_due: '#f59e0b', // amber-500
    grace_period: '#f59e0b', // amber-500
    suspended: '#ef4444', // red-500
    pending_card: '#94a3b8', // slate-400
  };
  return colors[status] ?? '#94a3b8';
}
